// FLAGS: identity, personal-data
// Expert profile panel plus the support actions on that expert's account.
import Button from "@openup/shared/components/Button/Button";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getTranslatedThemeName } from "@src/utils/languageHelpers";
import {
  usePsychologistQuery,
  usePsychologistThemesQuery,
  useExpertDetailsMutation,
  useResetTwoFactorMutation,
  resyncCalendar,
} from "@src/queries/experts";
import {
  psychologistGenderTagName,
  psychologistLanguageTagName,
  psychologistStatusTagName,
  sessionTypeTagName,
  useUserTagsQuery,
} from "@src/queries/tags";
import { formatNameAvatar } from "@src/utils/formatter";
import authenticationService from "@src/services/authentication.service";
import { useClientUnlockMutation } from "@src/queries/clients";
import {
  WithSnackbarProps,
  withSnackbar,
} from "@src/components/SnackBarComponent";
import { ResponsePsychologistResponse } from "@src/api/v1/data-contracts";
import { withSuspense } from "./wrappers/Suspendable";
import ConfirmActionDialog from "./ConfirmActionDialog";
import UpdatePsychologistTagsDialog from "./UpdatePsychologistDialog";
import LanguageAvailabilityDialog from "./LanguageAvailabilityDialog/LanguageAvailabilityDialog";

interface PsychologistDetailsProps extends WithSnackbarProps {
  viewUpdated: boolean;
  setViewUpdated: React.Dispatch<React.SetStateAction<boolean>>;
  psychologistId: string;
  className?: string;
  refetchPsychologistsList?: () => void;
  isEditAllowed: boolean;
}

const PsychologistDetails: React.FC<PsychologistDetailsProps> = ({
  viewUpdated,
  setViewUpdated,
  psychologistId,
  className = "",
  refetchPsychologistsList = undefined,
  isEditAllowed,
  snackbarShowMessage,
}) => {
  const { data: psychologist } = usePsychologistQuery(psychologistId);
  const { psychologistThemes } = usePsychologistThemesQuery(psychologistId);
  const { t } = useTranslation();
  const { isSupport } = authenticationService;
  const { mutateAsync: unlockMutation } = useClientUnlockMutation();
  const {
    mutateAsync: toggleAiSessionNotes,
    isPending: aiSessionNotesLoading,
  } = useExpertDetailsMutation();
  const {
    mutateAsync: toggleTwentyFourSeven,
    isPending: twentyFourSevenLoading,
  } = useExpertDetailsMutation();
  const { mutateAsync: resetTwoFactorMutation } = useResetTwoFactorMutation();

  const { userTags: userPsychologistStatusTags } = useUserTagsQuery(
    psychologistId,
    psychologistStatusTagName,
  );
  const { userTags: userSessionTypeTags } = useUserTagsQuery(
    psychologistId,
    sessionTypeTagName,
  );
  const { userTags: userLanguageTags } = useUserTagsQuery(
    psychologistId,
    psychologistLanguageTagName,
  );
  const { userTags: userGenderTags } = useUserTagsQuery(
    psychologistId,
    psychologistGenderTagName,
  );

  const [showDialog, setShowDialog] = useState(false);
  const [resyncCalendarLoading, setResyncCalendarLoading] = useState(false);
  const [unlockUserLoading, setUnlockUserLoading] = useState(false);
  const [reset2FALoading, setReset2FALoading] = useState(false);
  const [showReset2FADialog, setShowReset2FADialog] = useState(false);
  const [showLanguageAvailabilityDialog, setShowLanguageAvailabilityDialog] =
    useState(false);

  useEffect(() => {
    setViewUpdated(!viewUpdated);
  }, [userPsychologistStatusTags, userSessionTypeTags, userLanguageTags]);

  // FLAGS: identity
  // Clears the lockout on a staff account. The button is only hidden by the client-side isSupport()
  // check further down, and both outcomes report an empty snackbar message, so a refused unlock looks
  // the same as a successful one.
  const handleUnlockUser = async (
    event: React.MouseEvent<HTMLButtonElement>,
    userId: string,
  ) => {
    event.stopPropagation();
    setUnlockUserLoading(true);
    try {
      await unlockMutation(userId);
      snackbarShowMessage("", "success");
      // eslint-disable-next-line sonarjs/no-ignored-exceptions, @typescript-eslint/no-unused-vars
    } catch (error) {
      snackbarShowMessage("", "error");
    } finally {
      setUnlockUserLoading(false);
    }
  };
  // FLAGS: health-data
  // Flips whether this expert's sessions may be transcribed into AI session notes, so it governs
  // whether new transcripts of therapy conversations get produced at all; failures show only an empty
  // error toast.
  const handleToggleAiSessionNotes = async (
    event: React.MouseEvent<HTMLButtonElement>,
    expert?: ResponsePsychologistResponse,
  ) => {
    event.stopPropagation();
    if (!expert) return;

    try {
      await toggleAiSessionNotes({
        userId: expert.id!,
        isTranscriptionAllowed: !expert.isTranscriptionAllowed,
      });
      snackbarShowMessage("", "success");
      // eslint-disable-next-line sonarjs/no-ignored-exceptions, @typescript-eslint/no-unused-vars
    } catch (error) {
      snackbarShowMessage("", "error");
    }
  };
  const handleResyncCalendar = async (
    event: React.MouseEvent<HTMLButtonElement>,
    expert: ResponsePsychologistResponse,
  ) => {
    event.stopPropagation();
    setResyncCalendarLoading(true);

    try {
      await resyncCalendar(expert.id);
      snackbarShowMessage("", "success");
    } catch (error) {
      snackbarShowMessage(
        error.code === "400"
          ? "Sync failed as users' token has expired.  Ask user to re-sync their Google calendar themselves"
          : "Re-sync failed",
        "error",
      );
    } finally {
      setResyncCalendarLoading(false);
    }
  };
  const handleToggleTwentyFourSeven = async (
    event: React.MouseEvent<HTMLButtonElement>,
    expert: ResponsePsychologistResponse,
  ) => {
    event.stopPropagation();

    try {
      await toggleTwentyFourSeven({
        userId: expert.id!,
        worksTwentyFourSeven: !expert.worksTwentyFourSeven,
      });
      snackbarShowMessage("", "success");
      // eslint-disable-next-line sonarjs/no-ignored-exceptions, @typescript-eslint/no-unused-vars
    } catch (error) {
      snackbarShowMessage("", "error");
    }
  };
  // FLAGS: identity
  // Disables the expert's second factor and drops their authentication tokens; the confirm dialog is
  // the only guard on this side, and the server policy is what actually authorises it.
  const handleReset2FAConfirmed = async () => {
    setReset2FALoading(true);
    try {
      await resetTwoFactorMutation(psychologist?.id);
      snackbarShowMessage(t("PsychologistDetails.Reset2FASuccess"), "success");
      setShowReset2FADialog(false);
    } catch (error) {
      const axiosError = error as AxiosError;
      snackbarShowMessage(axiosError.message || "", "error");
    } finally {
      setReset2FALoading(false);
    }
  };
  const dividerClass = "mt-5 mb-5 border-0 h-px bg-white opacity-20";
  const captionsClass = "cap1 text-blue-500 mb-2";
  const textClass = "body2 text-white";
  return (
    <div
      className={`rounded px-4 py-3 bg-indigo-800 min-w-[265px] ${className}`}
    >
      <div className="flex items-center justify-center w-[132px] h-[132px] mb-5 rounded-full bg-gray-400 text-white text-4xl">
        {formatNameAvatar(psychologist?.firstName, psychologist?.lastName)}
      </div>
      <div className="mb-4 subtitle1 text-white">
        {psychologist?.firstName}, {psychologist?.lastName}
      </div>
      <p className={textClass}>{psychologist?.email}</p>
      <hr className={dividerClass} />
      <p className={captionsClass}>{t("Tags.PsychologistStatusTagsTitle")}</p>
      <div className={textClass}>
        {userPsychologistStatusTags?.map((tag) => tag.description)}
      </div>
      <hr className={dividerClass} />
      <p className={captionsClass}>{t("Tags.SessionTypeTagsTitle")}</p>
      <div className={textClass}>
        {userSessionTypeTags?.map(
          (tag, index) => (index ? ", " : "") + tag.description,
        )}
      </div>
      <hr className={dividerClass} />
      <p className={captionsClass}>{t("Tags.LanguageTagsTitle")}</p>
      <div className={textClass}>
        {userLanguageTags?.map(
          (tag, index) => (index ? ", " : "") + tag.description,
        )}
      </div>
      <hr className={dividerClass} />
      <p className={captionsClass}>{t("Common.Gender")}</p>
      <div className={textClass}>
        {userGenderTags?.map(
          (tag, index) => (index ? ", " : "") + tag.description,
        )}
      </div>
      <hr className={dividerClass} />
      <p className={captionsClass}>{t("SharedStrings.Themes")}</p>
      <div className={textClass}>
        {psychologistThemes?.map(
          (tag, index) =>
            (index ? ", " : "") + t(getTranslatedThemeName(t, tag.key || null)),
        )}
      </div>
      <hr className={dividerClass} />
      {isSupport() && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          {psychologist?.isLockedOut && (
            <Button
              variant="primary"
              data-cy="lock-account"
              onClick={(event) => handleUnlockUser(event, psychologist?.id)}
              className="w-full px-0"
              loading={unlockUserLoading}
            >
              {t("Common.UnlockAccount")}
            </Button>
          )}
          <Button
            variant={
              psychologist?.worksTwentyFourSeven ? "tertiary" : "primary"
            }
            disabled={!psychologist?.hasLinkedCalendar}
            data-cy="247-toggle-button"
            onClick={(event) =>
              handleToggleTwentyFourSeven(event, psychologist)
            }
            className={`${
              psychologist?.worksTwentyFourSeven
                ? "outline-white text-white"
                : ""
            } w-full px-0`}
            classNameLoading="text-white"
            loading={twentyFourSevenLoading}
          >
            {t(
              psychologist?.worksTwentyFourSeven
                ? "PsychologistDetails.Disable247"
                : "PsychologistDetails.Enable247",
            )}
          </Button>
          <Button
            variant={
              psychologist?.isTranscriptionAllowed ? "tertiary" : "primary"
            }
            data-cy="ai-session-notes-button"
            onClick={(event) => handleToggleAiSessionNotes(event, psychologist)}
            className={`${
              psychologist?.isTranscriptionAllowed
                ? "outline-white text-white"
                : ""
            } w-full px-0 mb-4`}
            classNameLoading="text-white"
            loading={aiSessionNotesLoading}
          >
            {t(
              psychologist?.isTranscriptionAllowed
                ? "PsychologistDetails.DisableAISessionNotes"
                : "PsychologistDetails.EnableAISessionNotes",
            )}
          </Button>

          <Button
            variant="primary"
            disabled={!psychologist?.hasLinkedCalendar}
            data-cy="resync-calendar-button"
            onClick={(event) => handleResyncCalendar(event, psychologist)}
            className="w-full px-0"
            loading={resyncCalendarLoading}
          >
            {t("PsychologistDetails.ResyncCalendar")}
          </Button>
          <Button
            variant="primary"
            data-cy="reset-2fa-button"
            onClick={() => setShowReset2FADialog(true)}
            className="w-full px-0"
          >
            {t("PsychologistDetails.Reset2FA")}
          </Button>
        </div>
      )}

      {isEditAllowed && (
        <Button
          variant="tertiary"
          onClick={() => setShowLanguageAvailabilityDialog(true)}
          className="outline-white text-white w-full mb-3"
          disabled={!psychologist?.id}
        >
          {t("LanguageAvailability.Button")}
        </Button>
      )}
      {isEditAllowed && (
        <Button
          variant="tertiary"
          onClick={() => setShowDialog(true)}
          className="outline-white text-white w-full"
          disabled={!psychologist?.id}
        >
          {t("Common.EditPsychologist")}
        </Button>
      )}

      {psychologist?.id && (
        <UpdatePsychologistTagsDialog
          isEdit
          open={showDialog}
          refetchPsychologistsList={refetchPsychologistsList}
          onClose={() => {
            setShowDialog(false);
          }}
          client={psychologist}
        />
      )}
      {psychologist?.id && showLanguageAvailabilityDialog && (
        <LanguageAvailabilityDialog
          open={showLanguageAvailabilityDialog}
          onClose={() => setShowLanguageAvailabilityDialog(false)}
          expertId={psychologist.id}
        />
      )}
      <ConfirmActionDialog
        open={showReset2FADialog}
        onCancelled={() => setShowReset2FADialog(false)}
        onConfirmed={handleReset2FAConfirmed}
        title={t("PsychologistDetails.Reset2FADialog.Title")}
        description={t("PsychologistDetails.Reset2FADialog.Description")}
        submitting={reset2FALoading}
        data-cy="reset-2fa-dialog"
      />
    </div>
  );
};

export default withSnackbar(
  withSuspense(PsychologistDetails, {
    nothingFoundTranslationKey: "Client.Details.NotFound",
  }),
);
